import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { Colors, Shadows } from '../constants/theme';
import { RootState } from '../store';
import { selectIsAuthenticated, selectCurrentUser } from '../store/authSlice';
import { media } from '../utils/responsive';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import LoginScreen from '../components/auth/LoginScreen';
import PowerBISidebar from '../components/PowerBISidebar';
import PowerBIHeader from '../components/PowerBIHeader';
import DashboardScreen from '../screens/DashboardScreen';
import TalentInsightsScreen from '../screens/TalentInsightsScreen';
import EmployeeProfileScreen from '../screens/EmployeeProfileScreen';
import DataSourcesScreen from '../screens/DataSourcesScreen';
import RecruiterDashboardScreen from '../screens/RecruiterDashboardScreen';
import RequisitionsScreen from '../screens/RequisitionsScreen';
import CandidateListScreen from '../screens/CandidateListScreen';
import CandidateProfileScreen from '../screens/CandidateProfileScreen';
import MarketIntelligenceScreen from '../screens/MarketIntelligenceScreen';
import AdministrationScreen from '../screens/AdministrationScreen';
import BelongingHubScreen from '../screens/BelongingHubScreen';
import BelongingResourcesScreen from '../screens/BelongingResourcesScreen';
import BelongingManagerInsightsScreen from '../screens/BelongingManagerInsightsScreen';
import EmployeeAnalyticsScreen from '../screens/EmployeeAnalyticsScreen';
import BrandedFooter from '../components/BrandedFooter';

// PowerBI Workspace Layout
const PowerBIWorkspace = styled.div`
  display: flex;
  height: 100vh;
  background-color: ${Colors.background};
  color: ${Colors.textPrimary};
  overflow: hidden;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${Colors.backgroundDark};
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentCanvas = styled.div`
  flex: 1;
  overflow-y: auto;
  background-color: ${Colors.background};
  padding: 0;
  position: relative;
`;

// Unauthorized access component
const UnauthorizedScreen: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column',
    alignItems: 'center', 
    justifyContent: 'center', 
    height: '100vh',
    textAlign: 'center',
    padding: '20px'
  }}>
    <h1 style={{ color: Colors.danger, marginBottom: '16px' }}>Access Denied</h1>
    <p style={{ color: Colors.textSecondary, marginBottom: '24px' }}>
      You don't have permission to access this resource.
    </p>
    <button 
      onClick={() => window.history.back()}
      style={{
        padding: '12px 24px',
        background: Colors.primary,
        color: Colors.textOnPrimary,
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer'
      }}
    >
      Go Back
    </button>
  </div>
);

// Main workspace layout component
const AuthenticatedWorkspace: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <PowerBIWorkspace>
      <PowerBISidebar />
      
      <MainContent>
        <PowerBIHeader />
        
        <ContentArea>
          <ContentCanvas>
            {children}
          </ContentCanvas>
          <BrandedFooter />
        </ContentArea>
      </MainContent>
    </PowerBIWorkspace>
  );
};

const AppRouter: React.FC = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser = useSelector(selectCurrentUser);

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/unauthorized" element={<UnauthorizedScreen />} />
        
        {/* Protected routes */}
        <Route path="/*" element={
          <ProtectedRoute>
            <AuthenticatedWorkspace>
              <Routes>
                {/* Default redirect based on role */}
                <Route 
                  path="/" 
                  element={
                    <Navigate 
                      to={currentUser?.role === 'RECRUITER' ? '/recruitment' : '/dashboard'} 
                      replace 
                    />
                  } 
                />
                
                {/* Dashboard routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      {currentUser?.role === 'RECRUITER' ? <RecruiterDashboardScreen /> : <DashboardScreen />}
                    </ProtectedRoute>
                  } 
                />
                
                {/* HR Leader routes */}
                <Route 
                  path="/insights" 
                  element={
                    <ProtectedRoute requiredPermission="canViewAIInsights">
                      <TalentInsightsScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/employee-analytics" 
                  element={
                    <ProtectedRoute requiredPermission="canViewSensitiveMetrics">
                      <EmployeeAnalyticsScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute requiredRole="HR_LEADER">
                      <AdministrationScreen />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Manager and HR Leader routes */}
                <Route 
                  path="/profiles" 
                  element={
                    <ProtectedRoute>
                      <EmployeeProfileScreen />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Shared routes for authenticated users */}
                <Route 
                  path="/data-sources" 
                  element={
                    <ProtectedRoute>
                      <DataSourcesScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/belonging" 
                  element={
                    <ProtectedRoute>
                      <BelongingHubScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/belonging/resources" 
                  element={
                    <ProtectedRoute>
                      <BelongingResourcesScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/belonging/manager-insights" 
                  element={
                    <ProtectedRoute requiredPermission="canViewSensitiveMetrics">
                      <BelongingManagerInsightsScreen />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Recruiter routes */}
                <Route 
                  path="/recruitment" 
                  element={
                    <ProtectedRoute requiredRole="RECRUITER">
                      <RecruiterDashboardScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/requisitions" 
                  element={
                    <ProtectedRoute requiredPermission="canManageRequisitions">
                      <RequisitionsScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/requisitions/:id" 
                  element={
                    <ProtectedRoute requiredPermission="canManageRequisitions">
                      <RequisitionsScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/candidates" 
                  element={
                    <ProtectedRoute requiredPermission="canViewRecruitmentData">
                      <CandidateListScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/candidates/:id" 
                  element={
                    <ProtectedRoute requiredPermission="canViewRecruitmentData">
                      <CandidateProfileScreen />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/market-intelligence" 
                  element={
                    <ProtectedRoute requiredPermission="canViewRecruitmentData">
                      <MarketIntelligenceScreen />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Catch-all redirect */}
                <Route 
                  path="*" 
                  element={<Navigate to="/dashboard" replace />} 
                />
              </Routes>
            </AuthenticatedWorkspace>
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
};

export default AppRouter;